import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, aboutFaqs, testimonials } from '@/mocks/anime';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aboutBgImage = 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20anime%20studio%20interior%20with%20warm%20ambient%20lighting%2C%20wooden%20shelves%20filled%20with%20art%20books%2C%20soft%20golden%20light%20from%20window%2C%20creative%20workspace%20atmosphere%2C%20anime%20production%20background%20art%2C%20cozy%20and%20professional%20setting&width=1920&height=600&seq=about-hero-bg&orientation=landscape';

  return (
    <>
      <Navbar />

      <main className="pt-20 pb-10">
        {/* Page Header */}
        <section className="relative w-full h-[280px] md:h-[350px] overflow-hidden mb-10">
          <div className="absolute inset-0">
            <img
              src={aboutBgImage}
              alt="关于羞羞动漫"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 to-background-950"></div>
          <div className="relative z-10 flex items-center justify-center h-full">
            <div className="text-center px-4">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-400 text-sm md:text-base max-w-[500px] mx-auto">
                了解羞羞动漫的品牌故事与发展历程
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-[1000px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
              品牌介绍
            </h2>
            <div className="prose-custom text-foreground-300 text-sm leading-relaxed space-y-4">
              <p>
                {siteInfo.name}成立于{siteInfo.founded}年，是专为成熟动漫爱好者打造的一站式高清动漫内容平台。
                我们致力于为广大二次元爱好者提供最全面、最优质的动漫资源，涵盖最新番剧更新、经典动漫回顾、
                独家引进内容等多个维度，满足不同用户的多样化观影需求。
              </p>
              <p>
                自成立以来，{siteInfo.name}始终坚持「正版授权、高清品质、流畅体验」的核心理念。
                我们与国内外多家知名动漫制作公司和发行商建立了深度合作关系，确保平台上的每一部番剧都经过正版授权，
                让用户在享受高品质内容的同时，也能支持动漫产业的发展。
              </p>
              <p>
                经过多年的运营积累，{siteInfo.name}已经成长为拥有{siteInfo.totalUsers}注册用户、
                收录{siteInfo.totalAnime}部高清番剧的行业领先平台。我们的专业翻译团队每天更新{siteInfo.dailyUpdate}内容，
                确保用户能够第一时间观看最新的动漫作品。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-12 md:mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50 text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                  {siteInfo.totalAnime}
                </p>
                <p className="text-foreground-500 text-xs md:text-sm">高清番剧资源</p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50 text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">
                  {siteInfo.totalUsers}
                </p>
                <p className="text-foreground-500 text-xs md:text-sm">注册用户</p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50 text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                  {siteInfo.dailyUpdate}
                </p>
                <p className="text-foreground-500 text-xs md:text-sm">每日更新内容</p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50 text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">
                  {new Date().getFullYear() - parseInt(siteInfo.founded)}
                </p>
                <p className="text-foreground-500 text-xs md:text-sm">年行业深耕</p>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-foreground-100 font-medium text-sm">{item.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: item.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-foreground-400 text-sm leading-relaxed">{item.comment}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {aboutFaqs.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-900 border border-background-800/50 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-foreground-200 font-medium text-sm pr-4">
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 text-lg shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-foreground-500 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Info */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-foreground-100 font-medium text-sm mb-1">电子邮件</h3>
                <p className="text-foreground-500 text-sm">{siteInfo.email}</p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-3">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h3 className="text-foreground-100 font-medium text-sm mb-1">公司地址</h3>
                <p className="text-foreground-500 text-sm">{siteInfo.address}</p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: siteInfo.name,
            url: 'https://xixiudongman.com',
            description: siteInfo.description,
            email: siteInfo.email,
            address: {
              '@type': 'PostalAddress',
              addressLocality: '上海',
              addressCountry: 'CN',
            },
            foundingDate: siteInfo.founded,
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: aboutFaqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}